/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { csmVector, iterator } from '@framework/type/csmvector';

import { gl } from './lappglmanager';

/**
 * キャッシュデータの構造体
 */
interface CachedTextureData {
  originalImageData: string; // base64 encoded image
  processedCanvasData: string; // base64 encoded processed canvas
  width: number;
  height: number;
  processedWidth: number;
  processedHeight: number;
  timestamp: number;
}

/**
 * 軽量キャッシュデータの構造体（大きなテクスチャ用）
 */
interface LightweightCacheData {
  width: number;
  height: number;
  processedWidth: number;
  processedHeight: number;
  scale: number;
  timestamp: number;
  isLightweight: true;
}

/**
 * テクスチャ管理クラス
 * 画像読み込み、管理を行うクラス。
 */
export class LAppTextureManager {
  /**
   * コンストラクタ
   */
  constructor() {
    this._textures = new csmVector<TextureInfo>();
  }

  /**
   * Create a 1×1 transparent placeholder texture and invoke the callback.
   * Used when the real texture file is missing (404) so the model setup
   * doesn't hang waiting for a callback that never fires.
   */
  private createTransparentPlaceholder(
    fileName: string,
    usePremultiply: boolean,
    callback: (textureInfo: TextureInfo) => void
  ): void {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, 1, 1);
    }
    const placeholderImg = new Image();
    placeholderImg.width = 1;
    placeholderImg.height = 1;
    this.createWebGLTexture(canvas, fileName, placeholderImg, usePremultiply, callback);
  }

  /**
   * キャッシュキーを生成
   */
  private generateCacheKey(fileName: string, usePremultiply: boolean, maxTextureSize: number): string {
    return `live2d_texture_${fileName}_${usePremultiply}_${maxTextureSize}`;
  }

  /**
   * キャッシュからテクスチャデータを取得
   */
  private getCachedTexture(cacheKey: string): CachedTextureData | LightweightCacheData | null {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        return data as CachedTextureData | LightweightCacheData;
      }
    } catch (error) {
      console.warn('Failed to load cached texture:', error);
    }
    return null;
  }

  /**
   * テクスチャデータをキャッシュに保存
   */
  private saveCachedTexture(cacheKey: string, data: CachedTextureData | LightweightCacheData): void {
    try {
      const dataString = JSON.stringify(data);
      const sizeInMB = (dataString.length / 1024 / 1024);

      // Check if data is too large (>10MB per texture)
      if (sizeInMB > 10) {
        console.warn(`Texture too large to cache: ${sizeInMB.toFixed(2)}MB`);
        return;
      }

      // Check current cache size and clean if over 100MB
      const currentCacheSize = this.getCurrentCacheSizeMB();
      if (currentCacheSize > 100) {
        console.log(`Cache size is ${currentCacheSize.toFixed(2)}MB, cleaning up...`);
        this.cleanupOldCache();
      }

      // Try to save directly first
      try {
        localStorage.setItem(cacheKey, dataString);
        console.log(`Cached texture: ${cacheKey} (${sizeInMB.toFixed(2)}MB)`);
        return;
      } catch (quotaError) {
        console.warn('Cache quota exceeded, attempting cleanup...');

        // Clear old cache entries and try again
        this.cleanupOldCache();

        try {
          localStorage.setItem(cacheKey, dataString);
          console.log(`Cached texture after cleanup: ${cacheKey} (${sizeInMB.toFixed(2)}MB)`);
          return;
        } catch (secondError) {
          console.warn('Still no space after cleanup, skipping cache for this texture');
        }
      }
    } catch (error) {
      console.warn('Failed to cache texture:', error);
    }
  }

  /**
   * 古いキャッシュエントリをクリーンアップ
   */
  private cleanupOldCache(): void {
    try {
      const keys = Object.keys(localStorage);
      const textureKeys = keys.filter(key => key.startsWith('live2d_texture_'));

      // Get cache entries with timestamps
      const cacheEntries: { key: string; timestamp: number; size: number }[] = [];

      textureKeys.forEach(key => {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const data = JSON.parse(item);
            cacheEntries.push({
              key,
              timestamp: data.timestamp || 0,
              size: item.length
            });
          }
        } catch (e) {
          // Remove corrupted entries
          localStorage.removeItem(key);
        }
      });

      // Sort by timestamp (oldest first) and remove oldest 50%
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = Math.ceil(cacheEntries.length * 0.5);

      let removedSize = 0;
      for (let i = 0; i < toRemove && i < cacheEntries.length; i++) {
        localStorage.removeItem(cacheEntries[i].key);
        removedSize += cacheEntries[i].size;
      }

      console.log(`Cleaned up ${toRemove} old cache entries, freed ${(removedSize / 1024 / 1024).toFixed(2)}MB`);
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  /**
   * 解放する。
   */
  public release(): void {
    for (
      let ite: iterator<TextureInfo> = this._textures.begin();
      ite.notEqual(this._textures.end());
      ite.preIncrement()
    ) {
      gl.deleteTexture(ite.ptr().id);
    }
    this._textures = null;
  }

  /**
   * 軽量キャッシュパラメータを使用して画像を読み込み
   */
  private loadImageWithCachedParams(
    fileName: string,
    usePremultiply: boolean,
    lightweightData: LightweightCacheData,
    callback: (textureInfo: TextureInfo) => void
  ): void {
    const img = new Image();
    img.addEventListener('load', () => {
      // Use cached scale parameters to process image quickly
      const canvas = document.createElement('canvas');
      canvas.width = lightweightData.processedWidth;
      canvas.height = lightweightData.processedHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      }

      // Create WebGL texture directly without re-caching
      this.createWebGLTexture(canvas, fileName, img, usePremultiply, callback);
    }, { passive: true });
    img.addEventListener('error', () => {
      console.warn(`[LAppTextureManager] Failed to load texture (cached params): ${fileName}, using transparent placeholder`);
      this.createTransparentPlaceholder(fileName, usePremultiply, callback);
    }, { passive: true });
    img.src = fileName;
  }

  /**
   * キャッシュされたデータからテクスチャを作成
   */
  private createTextureFromCachedData(
    cachedData: CachedTextureData,
    fileName: string,
    usePremultiply: boolean,
    callback: (textureInfo: TextureInfo) => void
  ): void {
    // Create image from cached original data
    const img = new Image();
    img.addEventListener('load', () => {
      // Create canvas from cached processed data
      const canvas = document.createElement('canvas');
      canvas.width = cachedData.processedWidth;
      canvas.height = cachedData.processedHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        const processedImg = new Image();
        processedImg.addEventListener('load', () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(processedImg, 0, 0);

          // Create WebGL texture from cached canvas
          this.createWebGLTexture(canvas, fileName, img, usePremultiply, callback);
        }, { passive: true });
        processedImg.addEventListener('error', () => {
          console.warn(`[LAppTextureManager] Failed to load processed cached texture: ${fileName}, using transparent placeholder`);
          this.createTransparentPlaceholder(fileName, usePremultiply, callback);
        }, { passive: true });
        processedImg.src = cachedData.processedCanvasData;
      }
    }, { passive: true });
    img.addEventListener('error', () => {
      console.warn(`[LAppTextureManager] Failed to load original cached texture: ${fileName}, using transparent placeholder`);
      this.createTransparentPlaceholder(fileName, usePremultiply, callback);
    }, { passive: true });
    img.src = cachedData.originalImageData;
  }

  /**
   * 画像を処理してキャッシュに保存
   */
  private processAndCacheTexture(
    img: HTMLImageElement,
    fileName: string,
    usePremultiply: boolean,
    maxTextureSize: number,
    cacheKey: string,
    callback: (textureInfo: TextureInfo) => void
  ): void {
    // Create a canvas to ensure transparent background
    const canvas = document.createElement('canvas');

    // Cap at 2048 to prevent OOM when loading many large textures (e.g. 33×4096)
    const appMaxRes = LAppTextureManager._maxTextureResolution;
    const effectiveMaxSize = Math.min(maxTextureSize, appMaxRes);

    console.log(`GPU Max texture size: ${maxTextureSize}, app cap: ${appMaxRes}, effective: ${effectiveMaxSize}`);
    console.log(`Image size: ${img.width}x${img.height}`);

    // Calculate resize ratio if needed
    const maxDimension = Math.max(img.width, img.height);
    const needsResize = maxDimension > effectiveMaxSize;
    let scale = 1;

    if (needsResize) {
      scale = effectiveMaxSize / maxDimension;
      console.log(`Resizing texture by scale: ${scale}`);
    }

    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);

    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Clear canvas to transparent
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Draw image (preserves alpha if present), with scaling if needed
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }

    // For very large images, only cache processing parameters (lightweight)
    const estimatedFullCacheSize = (img.width * img.height * 4 * 2) / 1024 / 1024; // Rough estimate in MB

    if (estimatedFullCacheSize > 15) {
      console.log(`Large texture detected (${estimatedFullCacheSize.toFixed(2)}MB estimated), using lightweight cache`);
      const lightweightData: LightweightCacheData = {
        width: img.width,
        height: img.height,
        processedWidth: canvas.width,
        processedHeight: canvas.height,
        scale: canvas.width / img.width,
        timestamp: Date.now(),
        isLightweight: true
      };
      this.saveCachedTexture(cacheKey, lightweightData as any);
    } else {
      // Create full-size original canvas only for smaller textures that use full cache
      const originalCanvas = document.createElement('canvas');
      originalCanvas.width = img.width;
      originalCanvas.height = img.height;
      const originalCtx = originalCanvas.getContext('2d');
      if (originalCtx) {
        originalCtx.drawImage(img, 0, 0);
      }
      const cacheData: CachedTextureData = {
        originalImageData: originalCanvas.toDataURL('image/jpeg', 0.8),
        processedCanvasData: canvas.toDataURL('image/png'),
        width: img.width,
        height: img.height,
        processedWidth: canvas.width,
        processedHeight: canvas.height,
        timestamp: Date.now()
      };
      this.saveCachedTexture(cacheKey, cacheData as any);
    }

    // Create WebGL texture
    this.createWebGLTexture(canvas, fileName, img, usePremultiply, callback);
  }

  /**
   * WebGLテクスチャを作成
   */
  private createWebGLTexture(
    canvas: HTMLCanvasElement,
    fileName: string,
    img: HTMLImageElement,
    usePremultiply: boolean,
    callback: (textureInfo: TextureInfo) => void
  ): void {
    // テクスチャオブジェクトの作成
    const tex: WebGLTexture = gl.createTexture();

    // テクスチャを選択
    gl.bindTexture(gl.TEXTURE_2D, tex);

    // テクスチャにピクセルを書き込む
    gl.texParameteri(
      gl.TEXTURE_2D,
      gl.TEXTURE_MIN_FILTER,
      gl.LINEAR_MIPMAP_LINEAR
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Premult処理を行わせる
    if (usePremultiply) {
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
    }

    // Upload canvas (with transparency) to texture
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      canvas
    );

    // ミップマップを生成
    gl.generateMipmap(gl.TEXTURE_2D);

    // テクスチャをバインド
    gl.bindTexture(gl.TEXTURE_2D, null);

    const textureInfo: TextureInfo = new TextureInfo();
    if (textureInfo != null) {
      textureInfo.fileName = fileName;
      textureInfo.width = canvas.width; // Use canvas dimensions, not original image
      textureInfo.height = canvas.height; // Use canvas dimensions, not original image
      textureInfo.id = tex;
      textureInfo.img = img;
      textureInfo.usePremultply = usePremultiply;
      this._textures.pushBack(textureInfo);
    }

    callback(textureInfo);
  }

  /**
   * 画像読み込み
   *
   * @param fileName 読み込む画像ファイルパス名
   * @param usePremultiply Premult処理を有効にするか
   * @return 画像情報、読み込み失敗時はnullを返す
   */
  public createTextureFromPngFile(
    fileName: string,
    usePremultiply: boolean,
    callback: (textureInfo: TextureInfo) => void
  ): void {
    // search loaded texture already
    for (
      let ite: iterator<TextureInfo> = this._textures.begin();
      ite.notEqual(this._textures.end());
      ite.preIncrement()
    ) {
      if (
        ite.ptr().fileName == fileName &&
        ite.ptr().usePremultply == usePremultiply
      ) {
        // 2回目以降はキャッシュが使用される(待ち時間なし)
        // WebKitでは同じImageのonloadを再度呼ぶには再インスタンスが必要
        // 詳細：https://stackoverflow.com/a/5024181
        ite.ptr().img = new Image();
        ite
          .ptr()
          .img.addEventListener('load', (): void => callback(ite.ptr()), {
            passive: true
          });
        ite
          .ptr()
          .img.addEventListener('error', (): void => {
            console.warn(`[LAppTextureManager] Failed to reload cached texture: ${fileName}, using transparent placeholder`);
            this.createTransparentPlaceholder(fileName, usePremultiply, callback);
          }, {
            passive: true
          });
        ite.ptr().img.src = fileName;
        return;
      }
    }

    // Check GPU texture size limits first
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    const cacheKey = this.generateCacheKey(fileName, usePremultiply, maxTextureSize);

    // Try to load from cache first
    const cachedData = this.getCachedTexture(cacheKey);
    if (cachedData) {
      console.log(`Loading texture from cache: ${fileName}`);

      // Check if it's a lightweight cache (only processing params)
      if ('isLightweight' in cachedData) {
        console.log('Using lightweight cache - loading and processing image with cached parameters');
        this.loadImageWithCachedParams(fileName, usePremultiply, cachedData, callback);
      } else {
        // Full cache with image data
        this.createTextureFromCachedData(cachedData, fileName, usePremultiply, callback);
      }
      return;
    }

    console.log(`Loading texture from file: ${fileName}`);
    // データのオンロードをトリガーにする
    const img = new Image();
    img.addEventListener(
      'load',
      (): void => {
        this.processAndCacheTexture(img, fileName, usePremultiply, maxTextureSize, cacheKey, callback);
      },
      { passive: true }
    );
    img.addEventListener(
      'error',
      (): void => {
        console.warn(`[LAppTextureManager] Failed to load texture: ${fileName}, using transparent placeholder`);
        this.createTransparentPlaceholder(fileName, usePremultiply, callback);
      },
      { passive: true }
    );
    img.src = fileName;
  }

  /**
   * 画像の解放
   *
   * 配列に存在する画像全てを解放する。
   */
  public releaseTextures(): void {
    for (let i = 0; i < this._textures.getSize(); i++) {
      this._textures.set(i, null);
    }

    this._textures.clear();
  }

  /**
   * 画像の解放
   *
   * 指定したテクスチャの画像を解放する。
   * @param texture 解放するテクスチャ
   */
  public releaseTextureByTexture(texture: WebGLTexture): void {
    for (let i = 0; i < this._textures.getSize(); i++) {
      if (this._textures.at(i).id != texture) {
        continue;
      }

      this._textures.set(i, null);
      this._textures.remove(i);
      break;
    }
  }

  /**
   * 画像の解放
   *
   * 指定した名前の画像を解放する。
   * @param fileName 解放する画像ファイルパス名
   */
  public releaseTextureByFilePath(fileName: string): void {
    for (let i = 0; i < this._textures.getSize(); i++) {
      if (this._textures.at(i).fileName == fileName) {
        this._textures.set(i, null);
        this._textures.remove(i);
        break;
      }
    }
  }

  /**
   * キャッシュクリア
   */
  public clearTextureCache(): void {
    try {
      const keys = Object.keys(localStorage);
      const textureKeys = keys.filter(key => key.startsWith('live2d_texture_'));
      textureKeys.forEach(key => localStorage.removeItem(key));
      console.log(`Cleared ${textureKeys.length} cached textures`);
    } catch (error) {
      console.warn('Failed to clear texture cache:', error);
    }
  }

  /**
   * キャッシュサイズをチェック (MB単位)
   */
  private getCurrentCacheSizeMB(): number {
    try {
      const keys = Object.keys(localStorage);
      const textureKeys = keys.filter(key => key.startsWith('live2d_texture_'));
      let totalSize = 0;
      textureKeys.forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length;
        }
      });
      return totalSize / 1024 / 1024;
    } catch (error) {
      return 0;
    }
  }

  /**
   * キャッシュサイズを取得
   */
  public getCacheSize(): string {
    try {
      const keys = Object.keys(localStorage);
      const textureKeys = keys.filter(key => key.startsWith('live2d_texture_'));
      let totalSize = 0;
      textureKeys.forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length;
        }
      });
      return `${(totalSize / 1024 / 1024).toFixed(2)} MB (${textureKeys.length} textures)`;
    } catch (error) {
      return 'Unknown';
    }
  }

  /** App-level max texture resolution (per side). Default 2048 to prevent OOM. */
  static _maxTextureResolution: number = 2048;

  static setMaxTextureResolution(size: number): void {
    LAppTextureManager._maxTextureResolution = size;
    console.log(`[LAppTextureManager] Max texture resolution set to ${size}`);
  }

  _textures: csmVector<TextureInfo>;
}

/**
 * 画像情報構造体
 */
export class TextureInfo {
  img: HTMLImageElement; // 画像
  id: WebGLTexture = null; // テクスチャ
  width = 0; // 横幅
  height = 0; // 高さ
  usePremultply: boolean; // Premult処理を有効にするか
  fileName: string; // ファイル名
}
